import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { TrendUp, TrendDown, Wallet, ChartLine } from '@phosphor-icons/react';

export default function SalaryStatistics({ records, selectedMonth, selectedYear }) {
  // Calculate statistics
  const stats = useMemo(() => {
    if (!records || records.length === 0) {
      return {
        currentMonthTotal: 0,
        previousMonthTotal: 0,
        percentChange: 0,
        employeeCount: 0,
        periodBreakdown: [],
        chartData: [],
      };
    }

    // Filter current month records
    const currentMonthRecords = records.filter(
      r => r.month === selectedMonth && r.year === selectedYear
    );

    // Calculate current month total using DOCHOD (not przelew)
    const currentMonthTotal = currentMonthRecords.reduce((sum, r) => {
      return sum + (r.dochod || 0);
    }, 0);

    // Calculate previous month
    const prevMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;
    const prevYear = selectedMonth === 1 ? selectedYear - 1 : selectedYear;

    const previousMonthRecords = records.filter(
      r => r.month === prevMonth && r.year === prevYear
    );

    const previousMonthTotal = previousMonthRecords.reduce((sum, r) => {
      return sum + (r.dochod || 0);
    }, 0);

    // Calculate percentage change
    const percentChange = previousMonthTotal > 0
      ? ((currentMonthTotal - previousMonthTotal) / previousMonthTotal) * 100
      : 0;

    // Count unique employees
    const uniqueEmployees = new Set(currentMonthRecords.map(r => r.employee_id));
    const employeeCount = uniqueEmployees.size;

    // Period breakdown (01-15 and 16-30)
    const periodBreakdown = [];
    const firstHalf = currentMonthRecords.filter(r => 
      r.period_start && r.period_end && 
      parseInt(r.period_start) <= 15 && parseInt(r.period_end) === 15
    );
    const secondHalf = currentMonthRecords.filter(r => 
      r.period_start && r.period_end &&
      (parseInt(r.period_start) > 15 || (parseInt(r.period_start) === 15 && parseInt(r.period_end) === 30)) &&
      parseInt(r.period_end) >= 30
    );

    if (firstHalf.length > 0) {
      periodBreakdown.push({
        label: '01-15',
        total: firstHalf.reduce((sum, r) => sum + (r.dochod || 0), 0),
        count: new Set(firstHalf.map(r => r.employee_id)).size
      });
    }
    
    if (secondHalf.length > 0) {
      periodBreakdown.push({
        label: '16-30',
        total: secondHalf.reduce((sum, r) => sum + (r.dochod || 0), 0),
        count: new Set(secondHalf.map(r => r.employee_id)).size
      });
    }

    // Chart data (last 3 months)
    const monthNames = ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'];
    const chartData = [];
    
    for (let i = 2; i >= 0; i--) {
      let m = selectedMonth - i;
      let y = selectedYear;
      
      if (m <= 0) {
        m += 12;
        y -= 1;
      }
      
      const monthRecords = records.filter(r => r.month === m && r.year === y);
      const total = monthRecords.reduce((sum, r) => sum + (r.dochod || 0), 0);
      
      chartData.push({
        name: monthNames[m - 1],
        value: total
      });
    }

    return {
      currentMonthTotal,
      previousMonthTotal,
      percentChange,
      employeeCount,
      periodBreakdown,
      chartData,
    };
  }, [records, selectedMonth, selectedYear]);

  const monthNames = ['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec', 
                      'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'];
  const monthName = monthNames[selectedMonth - 1] || '';

  return (
    <div className="space-y-4 mb-6">
      {/* Unified summary — flat industrial style */}
      <div className="border border-border bg-white p-4 sm:p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div className="flex-1 min-w-0">
            <div className="overline mb-2">W sumie w {monthName}</div>
            <div className="font-mono text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight text-foreground">
              {stats.currentMonthTotal.toLocaleString('pl-PL', { minimumFractionDigits: 2 })}
              <span className="text-xl sm:text-2xl text-muted-foreground ml-2">zł</span>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3 text-sm">
              <span
                className={`inline-flex items-center gap-1.5 font-mono font-semibold px-2 py-0.5 border ${
                  stats.percentChange >= 0
                    ? 'border-[hsl(120_100%_27%)] text-[hsl(120_100%_27%)]'
                    : 'border-signal text-signal'
                }`}
              >
                {stats.percentChange >= 0 ? (
                  <TrendUp className="w-3.5 h-3.5" weight="bold" />
                ) : (
                  <TrendDown className="w-3.5 h-3.5" weight="bold" />
                )}
                {Math.abs(stats.percentChange).toFixed(1)}%
              </span>
              <span className="text-muted-foreground text-xs">vs poprzedni miesiąc</span>
              <span className="text-muted-foreground text-xs">·</span>
              <span className="font-mono text-foreground font-semibold">{stats.employeeCount}</span>
              <span className="text-muted-foreground text-xs">
                {stats.employeeCount === 1 ? 'pracownik' : 'pracowników'}
              </span>
            </div>
          </div>

          {/* Mini Chart — plain, no glassmorphism */}
          <div className="w-full lg:w-96 h-24 shrink-0 border border-border">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.chartData}>
                <defs>
                  <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--foreground))" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="hsl(var(--foreground))" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="value"
                    stroke="#ffffff"
                    strokeWidth={3}
                    fill="url(#areaGradient)"
                    dot={{ fill: '#ffffff', r: 4, strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                  />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: '#e0e7ff', fontWeight: 'bold' }}
                    stroke="#a5b4fc"
                    strokeWidth={2}
                  />
                  <Tooltip
                    formatter={(value) => [
                      `${value.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł`,
                      'Dochód',
                    ]}
                    contentStyle={{
                      fontSize: 12,
                      fontFamily: 'IBM Plex Mono, monospace',
                      backgroundColor: '#fff',
                      borderRadius: '2px',
                      border: '1px solid hsl(45 15% 87%)',
                      padding: '6px 10px',
                      boxShadow: 'none',
                    }}
                    labelStyle={{ fontWeight: 600, marginBottom: '2px' }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

        {/* Period breakdown — bento row */}
        {stats.periodBreakdown.length > 0 && (
          <div className="mt-6 pt-6 border-t border-border">
            <div className="overline mb-3">Wypłaty według okresów</div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {stats.periodBreakdown.map((period, idx) => (
                <div
                  key={idx}
                  className="border border-border p-3 hover:border-foreground/40 transition-colors"
                  data-testid={`period-card-${idx}`}
                >
                  <div className="overline mb-1">{period.label}</div>
                  <div className="font-mono text-lg font-medium tracking-tight">
                    {period.total.toLocaleString('pl-PL', { minimumFractionDigits: 2 })}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    <span className="font-mono font-semibold text-foreground">{period.count}</span>{' '}
                    {period.count === 1 ? 'pracownik' : 'pracowników'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
